import { useState, useEffect } from "react";
import { TrendingUp, Heart, Star, MessageCircle } from "lucide-react";
import { motion } from "motion/react";
import { University } from "../../../types/University";
import { Link } from "react-router-dom";
import { db } from "../../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

const countryFlagUrls: Record<string, string> = {
  Malaysia: new URL("../../../assets/logo/country/malaysia.png", import.meta.url).href,
  Singapore: new URL("../../../assets/logo/country/singapore.png", import.meta.url).href,
  Australia: new URL("../../../assets/logo/country/australia.png", import.meta.url).href,
  "United Kingdom": new URL("../../../assets/logo/country/uk.png", import.meta.url).href,
};

function getWhyTags(uni: University): string[] {
  const tags: string[] = [];
  if (uni.country === "Malaysia") tags.push("Suitable for local study");
  else tags.push("Suitable for overseas study");
  tags.push("SCS Education partner");
  return tags.slice(0, 2);
}

export default function UniversityCard({
  uni,
  saved,
  onToggleSave,
  isRecommended,
}: {
  uni: University;
  saved: boolean;
  onToggleSave: (slug: string) => void;
  isRecommended?: boolean;
}) {
  const [applying, setApplying] = useState(false);
  const whyTags = getWhyTags(uni);

  const [tuitionRange, setTuitionRange] = useState<{
    min: number;
    max: number;
  } | null>(null);

  useEffect(() => {
    async function fetchTuitionRange() {
      try {
        const q = query(
          collection(db, "course-listing"),
          where("uniSlug", "==", uni.slug),
        );
        const snapshot = await getDocs(q);
        const fees: number[] = [];

        snapshot.forEach((doc) => {
          const fee = doc.data().tuitionFee;
          if (typeof fee === "number" && !isNaN(fee)) {
            fees.push(fee);
          }
        });

        if (fees.length > 0) {
          setTuitionRange({
            min: Math.min(...fees),
            max: Math.max(...fees),
          });
        }
      } catch (err) {
        console.error("Failed to fetch tuition range:", err);
      }
    }

    fetchTuitionRange();
  }, [uni.slug]);

  // Build logo URL from filename stored in Firestore
  let logoUrl: string | null = null;
  if (uni.logo) {
    try {
      logoUrl = new URL(`../../../assets/images/${uni.logo}`, import.meta.url)
        .href;
    } catch {
      logoUrl = null;
    }
  }

  const countryFlagUrl = countryFlagUrls[uni.country] ?? null;

  // Derive a display intake from whichever intake arrays are populated
  const intakeDisplay =
    uni.degreeIntake?.join(", ") ||
    uni.diplomaIntake?.join(", ") ||
    uni.foundationIntake?.join(", ") ||
    "—";

  // Use first ranking string if available
  const rankingDisplay = uni.rankings?.[0] ?? "";
  const keyInfoDisplay = uni.keyInfo?.[0] ?? "";

  return (
    <motion.div
      layout
      className="bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-50">
        {/* {isRecommended && (
          <div className="flex items-center gap-1 mb-3">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span
              className="text-[10px] text-amber-600"
              style={{ fontWeight: 700 }}
            >
              RECOMMENDED FOR YOU
            </span>
          </div>
        )} */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={uni.universityName}
                className="w-12 h-12 object-contain rounded-xl bg-slate-50"
              />
            ) : (
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-[13px] flex-shrink-0 shadow-md bg-violet-600">
                {uni.universityName?.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <p
                className="text-[14px] text-slate-900 leading-snug"
                style={{ fontWeight: 700 }}
              >
                {uni.universityName}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {countryFlagUrl ? (
                  <img src={countryFlagUrl} alt={uni.country} className="w-5 h-4" />
                ) : null}
                <span
                  className="text-[11px] text-slate-500"
                  style={{ fontWeight: 400 }}
                >
                  {uni.country}
                </span>
              </div>
            </div>
          </div>
          {/* <button
            onClick={() => onToggleSave(uni.slug)}
            className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-all flex-shrink-0 ${
              saved
                ? "bg-red-50 border-red-200 text-red-500"
                : "bg-slate-50 border-slate-200 text-slate-400 hover:bg-red-50 hover:border-red-200 hover:text-red-400"
            }`}
          >
            <Heart className={`w-4 h-4 ${saved ? "fill-red-500" : ""}`} />
          </button> */}
        </div>

        {/* Ranking */}
        {rankingDisplay && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 w-fit mb-3">
            <TrendingUp className="w-3 h-3 text-violet-500" />
            <span
              className="text-[11px] text-slate-700"
              style={{ fontWeight: 600 }}
            >
              {rankingDisplay}
            </span>
          </div>
        )}

        {/* Key Info */}
        {keyInfoDisplay && (
          <div className="flex items-center gap-1 mt-2">
            <span
              className="text-[11px] text-slate-600 ml-1.5"
              style={{ fontWeight: 600 }}
            >
              {keyInfoDisplay}
            </span>
          </div>
        )}
      </div>

      <div className="px-5 flex flex-col gap-4 flex-1">
        {/* Fields / Courses */}
        {uni.courses && uni.courses.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1">
            {uni.courses.slice(0, 4).map((f) => {
              const words = f.trim().split(/\s+/);
              const display = words.length > 1 ? `${words[0]}...` : f;
              return (
                <span
                  key={f}
                  title={f}
                  className="text-[11px] text-slate-600 bg-slate-100 border border-slate-100 px-2.5 py-1 rounded-full"
                  style={{ fontWeight: 500 }}
                >
                  {display}
                </span>
              );
            })}
          </div>
        )}

        {/* Why recommended tags */}
        <div className="flex flex-wrap gap-1.5 mb-1">
          {whyTags.map((tag, i) => (
            <span
              key={i}
              className="text-[10px] px-2.5 py-1 rounded-full bg-violet-50 text-violet-600 border border-violet-100"
              style={{ fontWeight: 600 }}
            >
              ✓ {tag}
            </span>
          ))}
        </div>

        {/* Intake */}
        <div className="flex flex-col gap-1.5">
          {/* <div className="flex items-center gap-2">
            <span
              className="text-[12px] text-slate-500 w-14 flex-shrink-0"
              style={{ fontWeight: 500 }}
            >
              Fees
            </span>
            <span
              className="text-[12px] text-slate-800"
              style={{ fontWeight: 600 }}
            >
              {tuitionRange
                ? tuitionRange.min === tuitionRange.max
                  ? `RM ${tuitionRange.min.toLocaleString()}`
                  : `RM ${tuitionRange.min.toLocaleString()} – RM ${tuitionRange.max.toLocaleString()}`
                : "—"}
            </span>
          </div> */}
          <div className="flex items-center gap-2">
            <span
              className="text-[12px] text-slate-500 w-14 flex-shrink-0"
              style={{ fontWeight: 500 }}
            >
              Intake
            </span>
            <span
              className="text-[12px] text-slate-800"
              style={{ fontWeight: 600 }}
            >
              {intakeDisplay}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto pb-4">
          <Link
            to={`/dashboard/university/${uni.slug}`}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[12px] flex items-center justify-center gap-1.5 shadow-sm hover:shadow-md transition-all hover:-translate-y-px cursor-pointer"
            style={{ fontWeight: 600 }}
          >
            Show more details
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
